import { Select } from 'antd';
import Icon from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { Host, refreshHostDelay } from 'services/api/leyanBotHost';
import { networkNodesCache, networkNodeValueCache } from 'services/cache';
import { eventBus } from 'services/eventBus';
import { ReactComponent as NetworkIcon } from 'assets/icons/network.svg';
import { css, cx } from '@emotion/css';
import { patchCurrentHost } from 'services/api';
import { useTranslate } from '@tolgee/react';

const selectWrap = css`
  > .ant-select-selector {
    height: auto !important;
    padding: 0 !important;
  }

  .ant-select-selection-item {
    display: flex;
    align-items: center;
  }
`;

interface SignalProps {
  delay?: number;
}

const Signal = (props: SignalProps) => {
  const { delay = 0 } = props;

  const delayColor = useMemo(() => {
    if (delay <= 0) return '';
    if (delay <= 600) return 'text-success';
    if (delay <= 1200) return 'text-remind';

    return 'text-error';
  }, [delay]);

  return (
    <div className={cx('flex items-center gap-1 mb-1', delayColor)}>
      <Icon component={NetworkIcon} />
      <span className="text-[10px] leading-4 mt-0.5">{delay}ms</span>
    </div>
  );
};

export const NetworkInfo = () => {
  const { t } = useTranslate();
  const [hostValue, setHostValue] = useState<string>(networkNodeValueCache.get() ?? '');
  const [hostsDelay, setHostsDelay] = useState<Host[]>(networkNodesCache.get() ?? []);

  useEffect(() => {
    const hostListHandler = (hosts: Host[]) => {
      setHostsDelay([...hosts]);
    };
    const hostValueHandler = (host: string) => {
      setHostValue(host);
    };
    eventBus.on('HOST_LIST_CHANGE', hostListHandler);
    eventBus.on('HOST_VALUE_CHANGE', hostValueHandler);

    return () => {
      eventBus.off('HOST_LIST_CHANGE', hostListHandler);
      eventBus.off('HOST_VALUE_CHANGE', hostValueHandler);
    };
  }, []);

  return (
    <Select
      className={selectWrap}
      value={hostValue}
      showArrow={false}
      bordered={false}
      showSearch={false}
      dropdownMatchSelectWidth={false}
      optionLabelProp="label"
      onDropdownVisibleChange={(open) => {
        if (open) {
          refreshHostDelay();
        }
      }}
      onChange={(value) => {
        setHostValue(value);
        networkNodeValueCache.set(value);
        patchCurrentHost(value);
      }}
    >
      {hostsDelay.map(({ domain, delay }, index) => {
        return (
          <Select.Option key={domain} value={domain} label={<Signal delay={delay} />}>
            <div className="flex items-center gap-4">
              <div>
                {t('network_node')} {index + 1}
              </div>
              <Signal delay={delay} />
            </div>
          </Select.Option>
        );
      })}
    </Select>
  );
};
